// src/pages/ContactPage.jsx - Enhanced Black & White Design
import React, { useState } from 'react';
import { Mail, MessageSquare, MapPin, Phone, Clock, Send, CheckCircle, User, AlertCircle } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { useTranslation } from 'react-i18next';
import LogamiLogoSpinner from '../components/ui/LogamiLogoSpinner';

const ContactPage = () => {
  const { t } = useTranslation();

  // Scroll animations
  const [heroRef, heroIsVisible] = useScrollAnimation();
  const [formRef, formIsVisible] = useScrollAnimation();
  const [infoRef, infoIsVisible] = useScrollAnimation();

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    type: 'general'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 2000));

    setSubmitted(true);
    setIsSubmitting(false);

    // Reset form after 3 seconds
    setTimeout(() => {
      setSubmitted(false);
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
        type: 'general'
      });
    }, 3000);
  };

  const contactInfo = [
    {
      icon: Mail,
      title: t('contact.info.email.title'),
      value: 'support@logami.app',
      description: t('contact.info.email.desc'),
      link: 'mailto:support@logami.app'
    },
    {
      icon: Phone,
      title: t('contact.info.phone.title'),
      value: 'ComingSoon',
      description: t('contact.info.phone.desc'),
      link: null
    },
    {
      icon: MapPin,
      title: t('contact.info.address.title'),
      value: 'ComingSoon',
      description: t('contact.info.address.desc'),
      link: null
    },
    {
      icon: Clock,
      title: t('contact.info.hours.title'),
      value: t('contact.info.hours.value'),
      description: t('contact.info.hours.desc'),
      link: null
    }
  ];

  const inquiryTypes = [
    { value: 'general', label: t('contact.form.types.general') },
    { value: 'technical', label: t('contact.form.types.technical') },
    { value: 'exam', label: t('contact.form.types.exam') },
    { value: 'partnership', label: t('contact.form.types.partnership') },
    { value: 'feedback', label: t('contact.form.types.feedback') }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Enhanced */}
      <section
        ref={heroRef}
        className={`
          relative py-16 sm:py-24 bg-white overflow-hidden
          transition-all duration-1000
          ${heroIsVisible ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-8'}
        `}
      >
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            {/* Badge */}
            <div className="inline-block px-5 py-2 bg-black text-white text-xs font-bold tracking-widest uppercase rounded-full mb-6 animate-pulse">
              💬 {t('contact.badge')}
            </div>

            {/* Title */}
            <h1 className="font-space text-3xl sm:text-5xl md:text-6xl font-black tracking-tight text-black mb-6 leading-tight">
              {t('contact.title')}
            </h1>

            {/* Subtitle */}
            <p className="text-lg sm:text-xl text-black/70 leading-relaxed max-w-3xl mx-auto">
              {t('contact.subtitle')}
            </p>
          </div>
        </div>
      </section>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Information - Enhanced */}
          <div
            ref={infoRef}
            className={`
              lg:col-span-1 
              transition-all duration-1000 delay-200
              ${infoIsVisible ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-8'}
            `}
          >
            <div className="relative bg-white rounded-2xl border-[3px] border-black p-8 h-fit overflow-hidden group">
              {/* Decorative corners */}
              <div className="absolute top-0 left-0 w-12 h-12 border-t-4 border-l-4 border-black/10 rounded-tl-2xl" />
              <div className="absolute bottom-0 right-0 w-12 h-12 border-b-4 border-r-4 border-black/10 rounded-br-2xl" />

              <div className="relative z-10">
                <h2 className="font-space text-2xl font-black text-black mb-6 tracking-tight">
                  {t('contact.info.title')}
                </h2>
                <p className="text-black/70 mb-8 leading-relaxed">
                  {t('contact.info.description')}
                </p>

                <div className="space-y-6">
                  {contactInfo.map((info, index) => (
                    <div
                      key={index}
                      className={`
                        flex items-start space-x-4 group/item
                        transition-all duration-500
                        ${infoIsVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}
                      `}
                      style={{ transitionDelay: infoIsVisible ? `${index * 0.1 + 0.3}s` : '0s' }}
                    >
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center group-hover/item:scale-110 group-hover/item:rotate-6 transition-all duration-300">
                          <info.icon className="w-6 h-6 text-white" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xs font-bold text-black/60 uppercase tracking-wider mb-1">
                          {info.title}
                        </h3>
                        {info.link ? (
                          // --- FIX: Added opening <a> tag here ---
                          <a
                            href={info.link}
                            className="text-lg font-bold text-black hover:text-black/70 transition-colors duration-300 block"
                          >
                            {info.value}
                          </a>
                        ) : (
                          <p className="text-lg font-bold text-black">{info.value}</p>
                        )}
                        <p className="text-sm text-black/60 mt-1">{info.description}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Quick Stats */}
                <div className="mt-8 pt-8 border-t-2 border-black/10">
                  <div className="grid grid-cols-2 gap-6 text-center">
                    <div className="group/stat cursor-default">
                      <div className="text-3xl font-black text-black mb-2 group-hover/stat:scale-110 transition-transform duration-300">
                        24h
                      </div>
                      <div className="text-sm text-black/60 font-medium">
                        {t('contact.stats.response')}
                      </div>
                    </div>
                    <div className="group/stat cursor-default">
                      <div className="text-3xl font-black text-black mb-2 group-hover/stat:scale-110 transition-transform duration-300">
                        99%
                      </div>
                      <div className="text-sm text-black/60 font-medium">
                        {t('contact.stats.satisfaction')}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form - Enhanced */}
          <div
            ref={formRef}
            className={`
              lg:col-span-2 
              transition-all duration-1000 delay-400
              ${formIsVisible ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-8'}
            `}
          >
            <div className="relative bg-white rounded-2xl border-[3px] border-black shadow-2xl p-8 overflow-hidden">
              {/* Background gradient */}
              <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-50 opacity-50" />

              {/* Decorative corners */}
              <div className="absolute top-0 left-0 w-16 h-16 border-t-4 border-l-4 border-black/10 rounded-tl-2xl" />
              <div className="absolute bottom-0 right-0 w-16 h-16 border-b-4 border-r-4 border-black/10 rounded-br-2xl" />

              <div className="relative z-10">
                {submitted ? (
                  <div className="text-center py-12">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-black rounded-2xl mb-6 animate-bounce">
                      <CheckCircle className="w-10 h-10 text-white" />
                    </div>
                    <h3 className="font-space text-3xl font-black text-black mb-4 tracking-tight">
                      {t('contact.success.title')}
                    </h3>
                    <p className="text-lg text-black/70 max-w-md mx-auto leading-relaxed">
                      {t('contact.success.message')}
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="mb-8">
                      <h2 className="font-space text-2xl font-black text-black mb-2 tracking-tight">
                        {t('contact.form.title')}
                      </h2>
                      <p className="text-black/70">
                        {t('contact.form.description')}
                      </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                      {/* Inquiry Type */}
                      <div>
                        <label htmlFor="type" className="block text-sm font-bold text-black mb-2 uppercase tracking-wider">
                          {t('contact.form.type.label')}
                        </label>
                        <select
                          id="type"
                          name="type"
                          value={formData.type}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border-2 border-black/20 rounded-xl focus:ring-2 focus:ring-black focus:border-black transition-all duration-300 bg-white font-medium"
                          required
                        >
                          {inquiryTypes.map(type => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Name and Email Row */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="name" className="block text-sm font-bold text-black mb-2 uppercase tracking-wider">
                            {t('contact.form.name.label')}
                          </label>
                          <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                              <User className="h-5 w-5 text-black/40 group-focus-within:text-black transition-colors duration-300" />
                            </div>
                            <input
                              type="text"
                              id="name"
                              name="name"
                              value={formData.name}
                              onChange={handleInputChange}
                              placeholder={t('contact.form.name.placeholder')}
                              className="w-full pl-12 pr-4 py-3 border-2 border-black/20 rounded-xl focus:ring-2 focus:ring-black focus:border-black transition-all duration-300 bg-white font-medium placeholder-black/40"
                              required
                            />
                          </div>
                        </div>

                        <div>
                          <label htmlFor="email" className="block text-sm font-bold text-black mb-2 uppercase tracking-wider">
                            {t('contact.form.email.label')}
                          </label>
                          <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                              <Mail className="h-5 w-5 text-black/40 group-focus-within:text-black transition-colors duration-300" />
                            </div>
                            <input
                              type="email"
                              id="email"
                              name="email"
                              value={formData.email}
                              onChange={handleInputChange}
                              placeholder={t('contact.form.email.placeholder')}
                              className="w-full pl-12 pr-4 py-3 border-2 border-black/20 rounded-xl focus:ring-2 focus:ring-black focus:border-black transition-all duration-300 bg-white font-medium placeholder-black/40"
                              required
                            />
                          </div>
                        </div>
                      </div>

                      {/* Subject */}
                      <div>
                        <label htmlFor="subject" className="block text-sm font-bold text-black mb-2 uppercase tracking-wider">
                          {t('contact.form.subject.label')}
                        </label>
                        <input
                          type="text"
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleInputChange}
                          placeholder={t('contact.form.subject.placeholder')}
                          className="w-full px-4 py-3 border-2 border-black/20 rounded-xl focus:ring-2 focus:ring-black focus:border-black transition-all duration-300 bg-white font-medium placeholder-black/40"
                          required
                        />
                      </div>

                      {/* Message */}
                      <div>
                        <label htmlFor="message" className="block text-sm font-bold text-black mb-2 uppercase tracking-wider">
                          {t('contact.form.message.label')}
                        </label>
                        <textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleInputChange}
                          rows={6}
                          placeholder={t('contact.form.message.placeholder')}
                          className="w-full px-4 py-3 border-2 border-black/20 rounded-xl focus:ring-2 focus:ring-black focus:border-black transition-all duration-300 bg-white resize-none font-medium placeholder-black/40"
                          required
                        />
                      </div>

                      {/* Submit Button */}
                      <div>
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="group/btn relative w-full flex justify-center items-center px-6 py-4 bg-black text-white font-space font-black text-lg rounded-xl border-3 border-black overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl"
                        >
                          {/* Animated background */}
                          <div className="absolute inset-0 bg-gradient-to-r from-gray-800 via-black to-gray-800 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-500" style={{
                            backgroundSize: '200% 100%',
                            animation: 'shimmer 3s infinite'
                          }} />

                          {/* Button content */}
                          <span className="relative z-10 flex items-center justify-center">
                            {isSubmitting ? (
                              <>
                                <LogamiLogoSpinner size="md" className="mr-2" decorative={false} alt="Logami loading" />
                                {t('contact.form.submitting')}
                              </>
                            ) : (
                              <>
                                <Send className="w-5 h-5 mr-2 transition-transform duration-300 group-hover/btn:translate-x-1" />
                                {t('contact.form.submit')}
                              </>
                            )}
                          </span>
                        </button>
                      </div>
                    </form>

                    {/* Additional Info */}
                    <div className="mt-8 pt-6 border-t-2 border-black/10">
                      <div className="flex items-start gap-3 bg-black/5 rounded-xl p-4">
                        <AlertCircle className="w-5 h-5 text-black/60 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-black/70 leading-relaxed">
                          {t('contact.form.privacy')}
                        </p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;